import { NgClass } from '@angular/common'
import { AfterViewInit, Component, input, signal, computed, output, ElementRef, ViewChild, inject, effect } from '@angular/core'
import { EventDay, EventSlot } from '../../../../types/event.type'
import { EventTimingStatus } from '../../../../enums/event.enum'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { DeleteModalComponent } from '@app/v2/shared/components/upload/components/delete-modal/delete-modal.component'

/** Desktop slot tooltip shown only up to this duration (hours). */
const SLOT_DETAIL_TOOLTIP_MAX_DURATION_H = 3
const MIN_SLOT_DURATION_H = 1
const INACTIVE_DAY_OVERLAY_DEFAULT_DURATION_H = 7
type DragEdge = 'start' | 'end' | 'move'
type DaySlotContext = { day: EventDay; slots: EventSlot[]; slot: EventSlot }

@Component({
  selector: 'app-event-scheduler',
  standalone: true,
  imports: [NgClass, DropdownComponent, NgxTippyModule, DeleteModalComponent],
  templateUrl: './event-scheduler.component.html',
  styleUrl: './event-scheduler.component.scss'
})
export class EventSchedulerComponent implements AfterViewInit {
  private readonly el = inject(ElementRef)
  private readonly _modalService = inject(ModalService)
  readonly days = input<EventDay[]>([])
  readonly startTime = input<string>('00:00')
  readonly endTime = input<string>('23:59')
  readonly timezoneLabel = input<string>('')
  readonly savingDayId = input<string | null>(null)
  readonly addingSlotPreview = input<{ dayId: string; overlayFrom: string; overlayTo: string } | null>(null)
  readonly dayToggle = output<{ dayId: string; state: 'active' | 'inactive' }>()
  readonly noteBlur = output<{ dayId: string; info: string | null }>()
  readonly slotsChange = output<{ dayId: string; slots: EventSlot[] }>()
  readonly addSlot = output<{ dayId: string; startTime: string }>()
  readonly addSlotModalOpen = output<{ dayId: string }>()
  readonly slotRemove = output<{ dayId: string; slotIndex: number }>()
  readonly slotEditOpen = output<{ dayId: string; slotIndex: number }>()
  private _seenSlotKeysOnce = false
  private _prevSlotKeys = new Set<string>()
  private _prevDaySlotOrder = new Map<string, string[]>()
  readonly justAddedSlotKeys = signal<Set<string>>(new Set())
  readonly disableSlotLayoutTransition = signal(false)
  /** Slot edge connections keyed by `${dayId}|${from}|${to}`; recomputed when `days()` changes. */
  private readonly _slotEdgeConnectByKey = signal<Map<string, { prev: boolean; next: boolean }>>(new Map())
  private readonly _slotMenuEditOption: DropdownOption = {
    key: 'edit',
    value: 'Edit',
    prefixIcon: `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.8952 0.543555L9.77765 1.7239L12.3503 4.44117L13.4671 3.26082C13.636 3.08242 13.7701 2.87061 13.8615 2.63749C13.9529 2.40438 14 2.15452 14 1.90219C14 1.64986 13.9529 1.4 13.8615 1.16688C13.7701 0.933763 13.636 0.721956 13.4671 0.543555C13.1204 0.194636 12.66 0 12.1811 0C11.7023 0 11.2418 0.194636 10.8952 0.543555Z" fill="#1C64F2"/><path d="M11.3215 5.52776L8.74888 2.8105L5.31117 6.43838C5.20946 6.54561 5.1401 6.68225 5.11182 6.83107L4.72912 8.86901C4.70558 8.9931 4.71142 9.12142 4.74612 9.24255C4.78082 9.36369 4.84331 9.4739 4.92804 9.56339C5.01277 9.65288 5.11711 9.71889 5.2318 9.75554C5.34649 9.79219 5.46797 9.79835 5.58546 9.77349L7.51494 9.3662C7.65558 9.3362 7.78467 9.26294 7.886 9.15565L11.3215 5.52776Z" fill="#1C64F2"/><path d="M5.31117 6.43838C5.20946 6.54561 5.1401 6.68225 5.11182 6.83107L4.72912 8.86901C4.70558 8.9931 4.71142 9.12142 4.74612 9.24255C4.78082 9.36369 4.84331 9.4739 4.92804 9.56339C5.01277 9.65288 5.11711 9.71889 5.2318 9.75554C5.34649 9.79219 5.46797 9.79835 5.58546 9.77349L7.51494 9.3662C7.65558 9.3362 7.78467 9.26294 7.886 9.15565L11.3215 5.52776L8.74888 2.8105L5.31117 6.43838Z" fill="#1C64F2"/><path d="M8.58008 10.0532C8.37679 10.2676 8.11814 10.4138 7.83652 10.4736L5.26388 11.0169C5.02903 11.0664 4.78623 11.054 4.55702 10.9806C4.32781 10.9073 4.11929 10.7753 3.94994 10.5965C3.7806 10.4176 3.65567 10.1974 3.58624 9.95527C3.51681 9.71318 3.50503 9.45673 3.55194 9.20867L4.06632 6.49141C4.12291 6.19396 4.26135 5.92077 4.46429 5.70605L8.04387 1.92524C8.02397 1.8536 8.01368 1.77936 8.01332 1.7047H1.57662C1.15859 1.7051 0.757795 1.88068 0.462206 2.19288C0.166616 2.50509 0.000385278 2.92842 0 3.36994V12.3348C0.000385278 12.7763 0.166616 13.1996 0.462206 13.5118C0.757795 13.824 1.15859 13.9996 1.57662 14H10.0643C10.4823 13.9996 10.8831 13.824 11.1787 13.5118C11.4743 13.1996 11.6405 12.7763 11.6409 12.3348V6.82031L8.58008 10.0532Z" fill="#1C64F2"/></svg>`
  }
  private readonly _slotMenuRemoveIcon = `<svg width="13" height="14" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.1204 2.94737H9.26852V1.47368C9.26852 1.08284 9.11829 0.708001 8.85087 0.431632C8.58346 0.155263 8.22077 0 7.84259 0H4.99074C4.61256 0 4.24987 0.155263 3.98246 0.431632C3.71505 0.708001 3.56481 1.08284 3.56481 1.47368V2.94737H0.712963C0.523873 2.94737 0.342528 3.025 0.208822 3.16318C0.0751155 3.30137 0 3.48879 0 3.68421C0 3.87963 0.0751155 4.06705 0.208822 4.20524C0.342528 4.34342 0.523873 4.42105 0.712963 4.42105H1.42593V12.5263C1.42593 12.9172 1.57616 13.292 1.84357 13.5684C2.11098 13.8447 2.47367 14 2.85185 14H9.98148C10.3597 14 10.7224 13.8447 10.9898 13.5684C11.2572 13.292 11.4074 12.9172 11.4074 12.5263V4.42105H12.1204C12.3095 4.42105 12.4908 4.34342 12.6245 4.20524C12.7582 4.06705 12.8333 3.87963 12.8333 3.68421C12.8333 3.48879 12.7582 3.30137 12.6245 3.16318C12.4908 3.025 12.3095 2.94737 12.1204 2.94737ZM4.99074 1.47368H7.84259V2.94737H4.99074V1.47368ZM5.7037 11.0526C5.7037 11.2481 5.62859 11.4355 5.49488 11.5737C5.36118 11.7118 5.17983 11.7895 4.99074 11.7895C4.80165 11.7895 4.62031 11.7118 4.4866 11.5737C4.35289 11.4355 4.27778 11.2481 4.27778 11.0526V5.89474C4.27778 5.69931 4.35289 5.5119 4.4866 5.37371C4.62031 5.23553 4.80165 5.15789 4.99074 5.15789C5.17983 5.15789 5.36118 5.23553 5.49488 5.37371C5.62859 5.5119 5.7037 5.69931 5.7037 5.89474V11.0526ZM8.55556 11.0526C8.55556 11.2481 8.48044 11.4355 8.34673 11.5737C8.21303 11.7118 8.03168 11.7895 7.84259 11.7895C7.6535 11.7895 7.47216 11.7118 7.33845 11.5737C7.20475 11.4355 7.12963 11.2481 7.12963 11.0526V5.89474C7.12963 5.69931 7.20475 5.5119 7.33845 5.37371C7.47216 5.23553 7.6535 5.15789 7.84259 5.15789C8.03168 5.15789 8.21303 5.23553 8.34673 5.37371C8.48044 5.5119 8.55556 5.69931 8.55556 5.89474V11.0526Z" fill="#E02424"/></svg>`
  private _lastPointerClientX: number | null = null
  private _edgeScrollRafId: number | null = null
  private readonly _edgeScrollMarginPx = 48
  private readonly _alwaysScrollbarMetrics = signal<{ scrollLeft: number; scrollWidth: number; clientWidth: number } | null>(null)
  private _alwaysScrollbarRaf: number | null = null

  constructor() {
    this._initialize()
  }

  ngAfterViewInit(): void {
    const grid = this.gridRef?.nativeElement
    if (!grid) return
    const onScroll = (): void => this._scheduleAlwaysScrollbarMetrics()
    grid.addEventListener('scroll', onScroll, { passive: true })
    this._scheduleAlwaysScrollbarMetrics()
  }

  slotMenuOptionsFor(row: EventDay & { dayNumber: number }): DropdownOption[] {
    return [
      this._slotMenuEditOption,
      {
        key: 'remove',
        value: 'Remove',
        prefixIcon: this._slotMenuRemoveIcon
      }
    ]
  }

  /**
   * @description Prepare for slot removal.
   */
  private _prepareForSlotRemoval(): void {
    this.disableSlotLayoutTransition.set(true)
    window.setTimeout(() => this.disableSlotLayoutTransition.set(false), 420)
  }

  /**
   * @description Remove a slot on mobile.
   * @param dayId - The ID of the day.
   * @param slotIndex - The index of the slot.
   */
  public onMobileRemoveSlot(dayId: string, slotIndex: number): void {
    if (this.isDayTimingsSaving(dayId)) return
    this._prepareForSlotRemoval()
    this.slotRemove.emit({ dayId, slotIndex })
  }

  private _slotKey(dayId: string, slot: EventSlot): string {
    return `${dayId}|${slot?.from ?? ''}|${slot?.to ?? ''}`
  }

  /**
   * Track key for the slot loop.
   * Must not depend on outer `row` to avoid control-flow reconciliation edge cases.
   */
  slotTrackKey(slot: EventSlot): string {
    return `${slot?.from ?? ''}|${slot?.to ?? ''}|${(slot?.label ?? '').trim()}`
  }

  isSlotJustAdded(dayId: string, slot: EventSlot): boolean {
    return this.justAddedSlotKeys().has(this._slotKey(dayId, slot))
  }

  /** Tippy on every slot: escape overflow/stacking in the scheduler grid */
  readonly slotDetailTippyProps = {
    theme: 'dark' as const,
    allowHTML: true,
    appendTo: () => document.body,
    zIndex: 200_000,
    maxWidth: 280
  }

  /** Tippy for the “+” hover add-slot button (must escape the scrollable grid). */
  readonly addSlotTippyProps = {
    theme: 'dark' as const,
    appendTo: () => document.body,
    zIndex: 200_000,
    placement: 'top' as const,
    delay: [150, 0] as const
  }

  readonly hoveredCell = signal<{ dayId: string; hour: number } | null>(null)
  readonly resizing = signal<{ dayId: string; slotIndex: number; edge: 'start' | 'end' | 'move' } | null>(null)
  /** Live preview during drag: fractional hours for bar position (smooth); labels use 15-minute grid and 23:59 at row end */
  readonly previewResize = signal<{ dayId: string; slotIndex: number; edge: 'start' | 'end' | 'move'; hours: number } | null>(null)

  readonly confirmDeactivateModalId = 'event-day-deactivate-modal'
  readonly pendingDeactivateDay = signal<{ id: string; dayNumber: number; label: string } | null>(null)

  /** `${dayId}-${slotIndex}` while that slot's actions menu is open (pressed / blue-600) */
  readonly slotMenuOpenKey = signal<string | null>(null)

  /** Selected day in mobile horizontal strip (`md` breakpoint hidden layout) */
  readonly selectedMobileDayId = signal<string | null>(null)

  @ViewChild('gridRef') gridRef!: ElementRef<HTMLElement>
  @ViewChild('timelineRef') timelineRef!: ElementRef<HTMLElement>
  @ViewChild('hScrollTrackRef') hScrollTrackRef!: ElementRef<HTMLElement>

  readonly timelineHeaderTranslateX = computed<string>(() => {
    const m = this._alwaysScrollbarMetrics()
    const leftPx = Math.max(0, m?.scrollLeft ?? 0)
    return `translateX(-${leftPx}px)`
  })

  readonly alwaysScrollbarThumbWidthPct = computed(() => {
    const m = this._alwaysScrollbarMetrics()
    if (!m || m.scrollWidth <= 0) return 100
    const w = (m.clientWidth / m.scrollWidth) * 100
    return Math.max(8, Math.min(100, w))
  })

  readonly alwaysScrollbarThumbLeftPct = computed(() => {
    const m = this._alwaysScrollbarMetrics()
    if (!m) return 0
    const range = Math.max(0, m.scrollWidth - m.clientWidth)
    if (range <= 0) return 0
    const widthPct = this.alwaysScrollbarThumbWidthPct()
    const travelPct = 100 - widthPct
    const t = Math.max(0, Math.min(1, m.scrollLeft / range))
    return t * travelPct
  })

  /** Full 24 hours on x-axis (0 = 00:00 through 23 = 23:00) */
  readonly hourColumns = computed(() => {
    const hours: number[] = []
    for (let h = 0; h < 24; h++) {
      hours.push(h)
    }
    return hours
  })

  /** Time range for positioning slots (full day 0–24) */
  private readonly _timeRange = { min: 0, max: 24 }
  readonly dayRows = computed(() => {
    return (this.days() ?? []).map((day, index) => ({
      ...day,
      dayNumber: index + 1,
      label: this._dayLabel(day),
      enabled: day.state !== EventTimingStatus.INACTIVE,
      note: day.info ?? ''
    }))
  })

  /** Map of day IDs to their index in the day rows */
  private readonly _dayIndexById = computed(() => {
    const map = new Map<string, number>()
    const rows = this.dayRows()
    for (let i = 0; i < rows.length; i++) {
      map.set(rows[i].id, i)
    }
    return map
  })

  /** Mobile detail card: row matching {@link selectedMobileDayId}, else first day */
  readonly mobileSelectedRow = computed(() => {
    const rows = this.dayRows()
    if (!rows.length) return null
    const id = this.selectedMobileDayId()
    const found = id ? rows.find((r) => r.id === id) : null
    return found ?? rows[0]
  })

  private _safeDayDate(day: Pick<EventDay, 'start_date'>): Date | null {
    const d = new Date(day.start_date + 'T12:00:00')
    return isNaN(d.getTime()) ? null : d
  }

  private _formatDayDate(day: Pick<EventDay, 'start_date'>, locale: string, options: Intl.DateTimeFormatOptions): string {
    const d = this._safeDayDate(day)
    return d ? d.toLocaleDateString(locale, options) : ''
  }

  private _parseTime(t: string): number {
    const [h, m] = (t || '00:00').split(':').map(Number)
    return (h ?? 0) + (m ?? 0) / 60
  }

  private _dayLabel(day: EventDay): string {
    const wd = this._formatDayDate(day, 'en-GB', { weekday: 'short' })
    const dm = this._formatDayDate(day, 'en-GB', { day: 'numeric', month: 'short' })
    if (!wd || !dm) return ''
    return `${wd}, ${dm}`
  }

  /** Slot header: "Day 1, Mon Jan 15, 2026" */
  slotDayHeader(row: EventDay & { dayNumber: number }): string {
    const dateStrWithComma = this._formatDayDate(row, 'en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    if (!dateStrWithComma) return `Day ${row.dayNumber}`
    const dateStr = dateStrWithComma.replace(/^(\w+),\s+/, '$1 ')
    return `Day ${row.dayNumber}, ${dateStr}`
  }

  /** Mobile chip: day of month */
  dayChipDayNumber(day: EventDay): string {
    const dayNum = this._formatDayDate(day, 'en-GB', { day: 'numeric' })
    return dayNum || ''
  }

  /** Mobile chip: short weekday */
  dayChipWeekday(day: EventDay): string {
    return this._formatDayDate(day, 'en-GB', { weekday: 'short' }) || ''
  }

  selectMobileDay(dayId: string): void {
    this.selectedMobileDayId.set(dayId)
  }

  isDayTimingsSaving(dayId: string): boolean {
    const id = this.savingDayId()
    return id != null && id === dayId
  }

  isDayAddSlotSavingUi(dayId: string): boolean {
    const p = this.addingSlotPreview()
    return this.isDayTimingsSaving(dayId) && p != null && p.dayId === dayId
  }

  addingSkeletonSlot(dayId: string): EventSlot | null {
    const p = this.addingSlotPreview()
    if (!p || p.dayId !== dayId) return null
    return { from: p.overlayFrom, to: p.overlayTo, label: '' }
  }

  /**
   * @description Hides the optimistic merged slot bar so only the skeleton shows for the in-flight add.
   */
  isSlotHiddenDuringAddSave(dayId: string, slot: EventSlot): boolean {
    if (!this.isDayAddSlotSavingUi(dayId)) return false
    const p = this.addingSlotPreview()
    if (!p) return false
    const overlay = this.slotTime({ from: p.overlayFrom, to: p.overlayTo, label: '' })
    const cur = this.slotTime(slot)
    return this._parseTime(overlay.start) === this._parseTime(cur.start) && this._parseTime(overlay.end) === this._parseTime(cur.end)
  }

  /**
   * @description Tippy props: suppress tooltip for add-save state and for slots longer than {@link SLOT_DETAIL_TOOLTIP_MAX_DURATION_H}.
   */
  tippyPropsForSlot(row: EventDay & { dayNumber: number }, slot: EventSlot, slotIndex: number) {
    const dur: number = this._slotDisplayDurationHours(row, slot, slotIndex)
    const allowTippy: boolean = !this.isDayAddSlotSavingUi(row.id) && dur > 0 && dur <= SLOT_DETAIL_TOOLTIP_MAX_DURATION_H
    return {
      ...this.slotDetailTippyProps,
      onShow: (): boolean => allowTippy
    }
  }

  onMobileOpenAddSlotModal(dayId: string): void {
    if (this.isDayTimingsSaving(dayId)) return
    this.addSlotModalOpen.emit({ dayId })
  }

  timeLabel(hour: number): string {
    const h = Math.floor(hour)
    const m = Math.round((hour - h) * 60)
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  }

  onDayToggle(dayId: string, dayNumber: number, enabled: boolean): void {
    if (this.isDayTimingsSaving(dayId)) return
    if (enabled) {
      this.dayToggle.emit({ dayId, state: 'active' })
      return
    }

    const days = this.days() ?? []
    const day = days.find((d) => d.id === dayId)
    const label = day ? this._dayLabel(day) : ''

    this.pendingDeactivateDay.set({ id: dayId, dayNumber, label })
    this._modalService.toggleModal(this.confirmDeactivateModalId)
  }

  confirmDeactivateDay(): void {
    const pending = this.pendingDeactivateDay()
    if (!pending) return
    this.dayToggle.emit({ dayId: pending.id, state: 'inactive' })
    this.pendingDeactivateDay.set(null)
  }

  cancelDeactivateDay(): void {
    this.pendingDeactivateDay.set(null)
    if (this._modalService.isModalOpen(this.confirmDeactivateModalId)) {
      this._modalService.toggleModal(this.confirmDeactivateModalId)
    }
  }

  onNoteBlur(dayId: string, value: string): void {
    if (this.isDayTimingsSaving(dayId)) return
    this.noteBlur.emit({ dayId, info: value?.trim() || null })
  }

  onNoteInputBlur(dayId: string, event: FocusEvent): void {
    const target = event.target as HTMLInputElement | null
    const value = target?.value ?? ''
    this.onNoteBlur(dayId, value)
  }

  /** Normalize slot to always have start/end (API may send from/to; empty string is treated as missing) */
  slotTime(slot: EventSlot | null | undefined): { start: string; end: string } {
    if (!slot) return { start: '', end: '' }
    const start = (slot.from ?? '').trim() || '00:00'
    let end = (slot.to ?? '').trim() || '23:59'
    if (end === '24:00' || end.startsWith('24:')) end = '23:59'
    return { start, end }
  }

  /** Check if the day has a slot that starts at the start of the day */
  private _dayHasStartOfDaySlot(day: (Pick<EventDay, 'slots'> & { enabled?: boolean }) | null | undefined): boolean {
    if (day && 'enabled' in day && day.enabled === false) return false
    const slots = day?.slots ?? []
    return slots.some((s) => this.slotTime(s).start === '00:00')
  }

  /** Check if the day has a slot that ends at the end of the day */
  private _dayHasEndOfDaySlot(day: (Pick<EventDay, 'slots'> & { enabled?: boolean }) | null | undefined): boolean {
    if (day && 'enabled' in day && day.enabled === false) return false
    const slots = day?.slots ?? []
    return slots.some((s) => this.slotTime(s).end === '23:59')
  }

  /**
   * Slot border radius override for cross-day connections.
   * Cheap lookup (map precomputed in an effect) so template doesn't scan adjacent days/slots.
   */
  public slotRadiusClass(dayId: string, slot: EventSlot): string {
    const key = this._slotKey(dayId, slot)
    const edge = this._slotEdgeConnectByKey().get(key)
    if (!edge) return ''
    if (edge.prev && edge.next) return 'tw-rounded-none'
    if (edge.prev) return 'tw-rounded-l-none'
    if (edge.next) return 'tw-rounded-r-none'
    return ''
  }

  /**
   * Back-compat for older template/build artifacts.
   * Prefer `slotRadiusClass()` in new bindings.
   */
  public slotConnectsToPrevDay(dayId: string, slot: EventSlot): boolean {
    const key = this._slotKey(dayId, slot)
    return this._slotEdgeConnectByKey().get(key)?.prev ?? false
  }

  /**
   * Back-compat for older template/build artifacts.
   * Prefer `slotRadiusClass()` in new bindings.
   */
  public slotConnectsToNextDay(dayId: string, slot: EventSlot): boolean {
    const key = this._slotKey(dayId, slot)
    return this._slotEdgeConnectByKey().get(key)?.next ?? false
  }

  slotPosition(slot: EventSlot | null | undefined): { left: number; width: number } {
    const { start, end } = this.slotTime(slot)
    if (!start || !end) return { left: 0, width: 0 }
    const startH = this._parseTime(start)
    const endH = this._parseTime(end)
    const { min, max } = this._timeRange
    const range = max - min
    const left = ((startH - min) / range) * 100
    const width = ((Math.min(endH, max) - startH) / range) * 100
    return { left: Math.max(0, left), width: Math.max(0, Math.min(width, 100 - left)) }
  }

  /** Left % and width % for the active time range (for inactive day overlay) */
  activeRangePosition(): { left: number; width: number } {
    const start = this.startTime() || '00:00'
    const end = this.endTime() || '23:59'
    const startH = this._parseTime(start)
    let endH = this._parseTime(end)
    if (!(endH > startH)) {
      endH = Math.min(24, startH + INACTIVE_DAY_OVERLAY_DEFAULT_DURATION_H)
    }
    const { min, max } = this._timeRange
    const range = max - min
    const left = ((startH - min) / range) * 100
    const width = ((Math.min(endH, max) - startH) / range) * 100
    return { left: Math.max(0, left), width: Math.max(0, Math.min(width, 100 - left)) }
  }

  slotLabel(day: EventDay, dayNumber: number, slot: EventSlot): string {
    const { start, end } = this.slotTime(slot)
    const dateStr = this._formatDayDate(day, 'en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' }) || day.start_date
    return `Day ${dayNumber}, ${dateStr}\n${start} - ${end}`
  }

  private _getTimelineRect(): DOMRect | null {
    const el = this.timelineRef?.nativeElement ?? this.gridRef?.nativeElement
    return el?.getBoundingClientRect() ?? null
  }

  private _updateAlwaysScrollbarMetrics(): void {
    const grid = this.gridRef?.nativeElement
    if (!grid) return
    this._alwaysScrollbarMetrics.set({
      scrollLeft: grid.scrollLeft,
      scrollWidth: grid.scrollWidth,
      clientWidth: grid.clientWidth
    })
  }

  private _scheduleAlwaysScrollbarMetrics(): void {
    if (this._alwaysScrollbarRaf !== null) return
    this._alwaysScrollbarRaf = requestAnimationFrame(() => {
      this._alwaysScrollbarRaf = null
      this._updateAlwaysScrollbarMetrics()
    })
  }

  onAlwaysScrollbarMouseDown(ev: MouseEvent): void {
    const grid = this.gridRef?.nativeElement
    const track = this.hScrollTrackRef?.nativeElement
    if (!grid || !track) return
    ev.preventDefault()
    ev.stopPropagation()

    const rect = track.getBoundingClientRect()
    const metrics = this._alwaysScrollbarMetrics() ?? {
      scrollLeft: grid.scrollLeft,
      scrollWidth: grid.scrollWidth,
      clientWidth: grid.clientWidth
    }
    const scrollRange = Math.max(0, metrics.scrollWidth - metrics.clientWidth)
    if (scrollRange <= 0) return

    const trackWidth = Math.max(1, rect.width)
    const thumbWidthPx = (this.alwaysScrollbarThumbWidthPct() / 100) * trackWidth
    const thumbTravel = Math.max(1, trackWidth - thumbWidthPx)
    const ratio = scrollRange / thumbTravel

    const startX = ev.clientX
    const startScrollLeft = grid.scrollLeft

    const move = (e: MouseEvent): void => {
      const deltaX = e.clientX - startX
      grid.scrollLeft = Math.max(0, Math.min(scrollRange, startScrollLeft + deltaX * ratio))
      this._scheduleAlwaysScrollbarMetrics()
    }
    const up = (): void => {
      document.removeEventListener('mousemove', move)
      document.removeEventListener('mouseup', up)
    }
    document.addEventListener('mousemove', move)
    document.addEventListener('mouseup', up)
  }

  private _cancelEdgeScrollLoop(): void {
    if (this._edgeScrollRafId !== null) {
      cancelAnimationFrame(this._edgeScrollRafId)
      this._edgeScrollRafId = null
    }
  }

  /**
   * While the pointer sits in the horizontal scroll container’s edge band, scroll the grid and
   * re-run the drag preview so the slot keeps tracking the cursor (same as native scroll-drag).
   */
  private _ensureEdgeScrollLoop(applyMove: (clientX: number) => void): void {
    if (this._edgeScrollRafId !== null) return
    const margin = this._edgeScrollMarginPx
    const tick = (): void => {
      this._edgeScrollRafId = null
      const grid = this.gridRef?.nativeElement
      const pointerClientX = this._lastPointerClientX
      if (!grid || pointerClientX == null || !this.resizing()) return

      const rect = grid.getBoundingClientRect()
      let delta = 0
      const minStep = 2
      const maxStep = 8
      if (pointerClientX < rect.left + margin) {
        const t = Math.min(1, (rect.left + margin - pointerClientX) / margin)
        delta = -Math.round(minStep + t * (maxStep - minStep))
      } else if (pointerClientX > rect.right - margin) {
        const t = Math.min(1, (pointerClientX - (rect.right - margin)) / margin)
        delta = Math.round(minStep + t * (maxStep - minStep))
      }

      const maxScroll = Math.max(0, grid.scrollWidth - grid.clientWidth)
      const before = grid.scrollLeft
      if (delta !== 0) {
        grid.scrollLeft = Math.max(0, Math.min(maxScroll, grid.scrollLeft + delta))
      }
      if (grid.scrollLeft !== before) {
        applyMove(pointerClientX)
      }

      const rect2 = grid.getBoundingClientRect()
      const lastPointerClientX = this._lastPointerClientX
      if (lastPointerClientX == null || !this.resizing()) return

      const inLeftEdge = lastPointerClientX < rect2.left + margin
      const inRightEdge = lastPointerClientX > rect2.right - margin
      const canLeft = grid.scrollLeft > 0.5
      const canRight = grid.scrollLeft < maxScroll - 0.5
      const canContinue = (inLeftEdge && canLeft) || (inRightEdge && canRight)
      if (canContinue) {
        this._edgeScrollRafId = requestAnimationFrame(tick)
      }
    }

    const grid = this.gridRef?.nativeElement
    if (!grid) return
    const rect = grid.getBoundingClientRect()
    const x = this._lastPointerClientX
    if (x == null) return
    const maxScroll = Math.max(0, grid.scrollWidth - grid.clientWidth)
    if (maxScroll <= 0) return
    if (x < rect.left + margin || x > rect.right - margin) {
      this._edgeScrollRafId = requestAnimationFrame(tick)
    }
  }

  /** Snap to 15-minute steps in decimal hours (0, 0.25, 0.5, …). */
  private _snapTo15Min(hours: number): number {
    return Math.round(hours * 4) / 4
  }

  /** `decimalHours` already snapped to {@link _snapTo15Min}; returns `HH:mm` (0–23:45). */
  private _clockFromSnappedDecimalHours(snapped: number): string {
    const capped = Math.min(23.75, Math.max(0, snapped))
    const totalMin = Math.round(capped * 60)
    const h = Math.floor(totalMin / 60)
    const m = totalMin % 60
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  }

  /** ~30 seconds in decimal hours — enough to treat snapped boundaries as touching */
  private readonly _slotTouchEpsilonH = 1 / 120

  private _mergeAdjacentSlotsAfterEdit(slots: EventSlot[], slotIndex: number): EventSlot[] {
    const merged = this._tryMergeAdjacentSlots(slots, slotIndex)
    return merged ?? slots
  }

  private _tryMergeAdjacentSlots(slots: EventSlot[], slotIndex: number): EventSlot[] | null {
    const slot = slots[slotIndex]
    if (!slot) return null
    const { start, end } = this.slotTime(slot)
    const newStartHours = this._parseTime(start)
    const newEndHours = this._parseTime(end)
    const moved: EventSlot = { ...slot, from: start, to: end, label: slot.label ?? '' }

    const epsilon = this._slotTouchEpsilonH
    const prev = slotIndex > 0 ? slots[slotIndex - 1] : undefined
    const next = slotIndex < slots.length - 1 ? slots[slotIndex + 1] : undefined
    const prevEnd = prev ? this._parseTime(this.slotTime(prev).end) : null
    const nextStart = next ? this._parseTime(this.slotTime(next).start) : null

    const touchesPrev = prev != null && prevEnd !== null && Math.abs(newStartHours - prevEnd) <= epsilon
    const touchesNext = next != null && nextStart !== null && Math.abs(newEndHours - nextStart) <= epsilon

    const joinSlotLabels = (...parts: (string | undefined)[]) =>
      parts
        .map((s) => s?.trim())
        .filter(Boolean)
        .join(' · ') || ''

    if (touchesPrev && touchesNext && prev && next) {
      const merged: EventSlot = {
        from: this.slotTime(prev).start,
        to: this.slotTime(next).end,
        label: joinSlotLabels(prev.label, moved.label, next.label)
      }
      const out = [...slots]
      out.splice(slotIndex - 1, 3, merged)
      return out
    }
    if (touchesNext && next) {
      const merged: EventSlot = {
        from: moved.from,
        to: this.slotTime(next).end,
        label: joinSlotLabels(moved.label, next.label)
      }
      const out = [...slots]
      out.splice(slotIndex, 2, merged)
      return out
    }
    if (touchesPrev && prev) {
      const merged: EventSlot = {
        from: this.slotTime(prev).start,
        to: moved.to,
        label: joinSlotLabels(prev.label, moved.label)
      }
      const out = [...slots]
      out.splice(slotIndex - 1, 2, merged)
      return out
    }
    return null
  }

  /**
   * Persisted slot `to` time after snap. The grid uses 0–24 as hours, but stored times must not use
   * `24:00` — end-of-day is always `23:59`.
   */
  private _toSlotEndClockString(decimalHours: number): string {
    const snapped = this._snapTo15Min(Math.max(0, Math.min(24, decimalHours)))
    if (snapped >= 24 - 1e-6) return '23:59'
    return this._clockFromSnappedDecimalHours(snapped)
  }

  /** Continuous 0–24h from pointer X — used while dragging so the bar tracks sub-pixel smoothly */
  private _clientXToHoursSmooth(clientX: number): number | null {
    const rect = this._getTimelineRect()
    if (!rect || rect.width <= 0) return null
    const relX = clientX - rect.left
    return Math.max(0, Math.min(24, (relX / rect.width) * 24))
  }

  private _neighborBoundsForSlot(dayId: string, slotIndex: number): { prevEnd: number; nextStart: number } {
    const day = (this.days() ?? []).find((d) => d.id === dayId)
    const slots = day?.slots ?? []
    const prev = slots[slotIndex - 1]
    const next = slots[slotIndex + 1]
    return {
      prevEnd: prev ? this._parseTime(this.slotTime(prev).end) : 0,
      nextStart: next ? this._parseTime(this.slotTime(next).start) : 24
    }
  }

  /**
   * @description Format the start label for the drag preview on the 15-minute grid; capped at 23:45.
   * @param hours - The hours to format.
   * @returns The formatted start label.
   */
  private _formatQuarterHourStartDragLabel(hours: number): string {
    let snapped = this._snapTo15Min(Math.max(0, Math.min(24, hours)))
    if (snapped >= 24) snapped = 23.75
    return this._clockFromSnappedDecimalHours(snapped)
  }

  /**
   * End label during drag: 15-minute grid until the slot reaches the open end of the row (`nextStart === 24`),
   * then show 23:59 when within one minute of the day end.
   */
  private _formatEndDragLabel(endHours: number, nextStart: number): string {
    if (nextStart >= 24 && endHours >= 24 - 1 / 60) return '23:59'
    let snapped = this._snapTo15Min(Math.max(0, Math.min(24, endHours)))
    if (snapped >= 24) snapped = 23.75
    return this._clockFromSnappedDecimalHours(snapped)
  }

  /**
   * @description Convert decimal hours to `HH:mm` format.
   * @param hours - The hours to convert.
   * @returns The `HH:mm` format of the hours.
   */
  private _clockFromDecimalHours(hours: number): string {
    const totalMin = Math.min(23 * 60 + 59, Math.round(Math.max(0, Math.min(24, hours)) * 60))
    const hour = Math.floor(totalMin / 60)
    const min = totalMin % 60
    return `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`
  }

  private _toHHmm(clock: string): string {
    return clock.slice(0, 5)
  }

  private _getDaySlotContext(dayId: string, slotIndex: number): DaySlotContext | null {
    const day = (this.days() ?? []).find((d) => d.id === dayId)
    if (!day) return null
    const slot = day.slots?.[slotIndex]
    if (!slot) return null
    return { day, slots: day.slots ?? [], slot }
  }

  private _beginDragSession(dayId: string, slotIndex: number, edge: DragEdge): void {
    this.resizing.set({ dayId, slotIndex, edge })
    document.body.classList.add('resizing-slot')
  }

  private _endDragSession(move: (e: MouseEvent) => void, up: (e: MouseEvent) => void): void {
    this._cancelEdgeScrollLoop()
    this._lastPointerClientX = null
    this.resizing.set(null)
    this.previewResize.set(null)
    document.body.classList.remove('resizing-slot')
    document.removeEventListener('mousemove', move)
    document.removeEventListener('mouseup', up)
  }

  private _attachPointerDrag(startEvent: MouseEvent, onApplyMove: (clientX: number) => void, onCommit: (clientX: number) => void): void {
    const move = (e: MouseEvent): void => {
      this._lastPointerClientX = e.clientX
      onApplyMove(e.clientX)
      this._ensureEdgeScrollLoop(onApplyMove)
    }
    const up = (e: MouseEvent): void => {
      this._endDragSession(move, up)
      onCommit(e.clientX)
    }
    move(startEvent)
    document.addEventListener('mousemove', move)
    document.addEventListener('mouseup', up)
  }

  /** Slot position for display: preview uses fractional hours while dragging for smooth motion */
  slotPositionWithPreview(row: { id: string }, slot: EventSlot, slotIndex: number): { left: number; width: number } {
    const preview = this.previewResize()
    const { start, end } = this.slotTime(slot)
    let startH = this._parseTime(start)
    let endH = this._parseTime(end)
    const originalDuration = endH - startH
    if (preview && preview.dayId === row.id && preview.slotIndex === slotIndex) {
      if (preview.edge === 'start') {
        startH = preview.hours
      } else if (preview.edge === 'end') {
        endH = preview.hours
      } else if (preview.edge === 'move') {
        startH = preview.hours
        endH = startH + originalDuration
      }
    }
    const { min, max } = this._timeRange
    const range = max - min
    const left = ((startH - min) / range) * 100
    const width = ((Math.min(endH, max) - startH) / range) * 100
    return { left: Math.max(0, left), width: Math.max(0, Math.min(width, 100 - left)) }
  }

  /** Time string for display; during drag uses the 15-minute grid (and 23:59 at row end) so labels stay stable */
  slotTimeDisplay(row: { id: string }, slot: EventSlot, slotIndex: number): { start: string; end: string } {
    const preview = this.previewResize()
    const { start, end } = this.slotTime(slot)
    let startH = this._parseTime(start)
    let endH = this._parseTime(end)
    const originalDuration = endH - startH
    if (preview && preview.dayId === row.id && preview.slotIndex === slotIndex) {
      const { nextStart } = this._neighborBoundsForSlot(row.id, slotIndex)
      if (preview.edge === 'start') {
        startH = preview.hours
        return {
          start: this._formatQuarterHourStartDragLabel(startH),
          end: this._clockFromDecimalHours(endH)
        }
      }
      if (preview.edge === 'end') {
        endH = preview.hours
        return {
          start: this._clockFromDecimalHours(startH),
          end: this._formatEndDragLabel(endH, nextStart)
        }
      }
      if (preview.edge === 'move') {
        startH = preview.hours
        endH = startH + originalDuration
        return {
          start: this._formatQuarterHourStartDragLabel(startH),
          end: this._formatEndDragLabel(endH, nextStart)
        }
      }
    }
    return { start: this._clockFromDecimalHours(startH), end: this._clockFromDecimalHours(endH) }
  }

  /** Display duration in hours (uses {@link slotTimeDisplay} so it matches drag preview). */
  private _slotDisplayDurationHours(row: { id: string }, slot: EventSlot, slotIndex: number): number {
    const { start, end } = this.slotTimeDisplay(row, slot, slotIndex)
    const a = this._parseTime(this._toHHmm(start))
    const b = this._parseTime(this._toHHmm(end))
    return Math.max(0, b - a)
  }

  /**
   * Rich tooltip for slots ≤3h, e.g. `Day 4, Thu Jan 18, 2026<br>00:00 - 01:00`.
   * Returns `undefined` when Tippy should be disabled (longer slots or invalid range).
   */
  slotDetailTippyContent(row: EventDay & { dayNumber: number }, slot: EventSlot, slotIndex: number): string | undefined {
    const duration = this._slotDisplayDurationHours(row, slot, slotIndex)
    if (duration <= 0 || duration > SLOT_DETAIL_TOOLTIP_MAX_DURATION_H) return undefined
    const datePart = this._formatDayDate(row, 'en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) || row.start_date
    const { start, end } = this.slotTimeDisplay(row, slot, slotIndex)
    const startHours = this._toHHmm(start)
    const endHours = this._toHHmm(end)
    return `Day ${row.dayNumber}, ${datePart}<br>${startHours} - ${endHours}`
  }

  /** Native title when Tippy is not used (wider slots). */
  slotFallbackTitle(row: EventDay & { dayNumber: number }, slot: EventSlot, slotIndex: number): string {
    const dur = this._slotDisplayDurationHours(row, slot, slotIndex)
    if (dur > 0 && dur <= SLOT_DETAIL_TOOLTIP_MAX_DURATION_H) return ''
    return this.slotLabel(row, row.dayNumber, slot)
  }

  startResize(dayId: string, slotIndex: number, $event: MouseEvent): void {
    $event.preventDefault()
    $event.stopPropagation()
    if (this.isDayTimingsSaving(dayId)) return
    const ctx = this._getDaySlotContext(dayId, slotIndex)
    if (!ctx) return
    const { slots, slot } = ctx
    const { end } = this.slotTime(slot)
    const endHours = this._parseTime(end)
    const prevSlot = slots[slotIndex - 1]
    const prevEnd = prevSlot ? this._parseTime(this.slotTime(prevSlot).end) : 0
    this._beginDragSession(dayId, slotIndex, 'start')

    const applyMove = (clientX: number): void => {
      const raw = this._clientXToHoursSmooth(clientX)
      if (raw == null) return
      const newStart = Math.max(prevEnd, Math.min(raw, endHours - MIN_SLOT_DURATION_H))
      this.previewResize.set({ dayId, slotIndex, edge: 'start', hours: newStart })
    }
    this._attachPointerDrag($event, applyMove, (clientX) => {
      const raw = this._clientXToHoursSmooth(clientX)
      if (raw == null) return
      let newStart = Math.max(prevEnd, Math.min(raw, endHours - MIN_SLOT_DURATION_H))
      newStart = this._snapTo15Min(newStart)
      const newSlots = [...slots]
      newSlots[slotIndex] = {
        ...slot,
        from: this._clockFromSnappedDecimalHours(newStart),
        to: this.slotTime(slot).end,
        label: slot.label ?? ''
      }
      this.slotsChange.emit({ dayId, slots: this._mergeAdjacentSlotsAfterEdit(newSlots, slotIndex) })
    })
  }

  endResize(dayId: string, slotIndex: number, $event: MouseEvent): void {
    $event.preventDefault()
    $event.stopPropagation()
    if (this.isDayTimingsSaving(dayId)) return
    const ctx = this._getDaySlotContext(dayId, slotIndex)
    if (!ctx) return
    const { slots, slot } = ctx
    const { start } = this.slotTime(slot)
    const startHours = this._parseTime(start)
    const nextSlot = slots[slotIndex + 1]
    const nextStart = nextSlot ? this._parseTime(this.slotTime(nextSlot).start) : 24
    this._beginDragSession(dayId, slotIndex, 'end')

    const applyMove = (clientX: number): void => {
      const raw = this._clientXToHoursSmooth(clientX)
      if (raw == null) return
      const newEnd = Math.max(startHours + MIN_SLOT_DURATION_H, Math.min(raw, nextStart))
      this.previewResize.set({ dayId, slotIndex, edge: 'end', hours: newEnd })
    }
    this._attachPointerDrag($event, applyMove, (clientX) => {
      const raw = this._clientXToHoursSmooth(clientX)
      if (raw == null) return
      let newEnd = Math.max(startHours + MIN_SLOT_DURATION_H, Math.min(raw, nextStart))
      newEnd = this._snapTo15Min(newEnd)
      const newSlots = [...slots]
      newSlots[slotIndex] = {
        ...slot,
        from: this.slotTime(slot).start,
        to: this._toSlotEndClockString(newEnd),
        label: slot.label ?? ''
      }
      this.slotsChange.emit({ dayId, slots: this._mergeAdjacentSlotsAfterEdit(newSlots, slotIndex) })
    })
  }

  /**
   * Drag after a small horizontal threshold moves the slot. A simple click (no drag) is handled by
   * `DropdownComponent`’s wrapper click on the full slot. Resize handles stop click propagation.
   */
  onSlotBodyMouseDown(dayId: string, slotIndex: number, $event: MouseEvent): void {
    $event.stopPropagation()
    if (this.isDayTimingsSaving(dayId)) return

    const thresholdPx = 5
    const startX = $event.clientX
    let dragged = false

    const onMove = (e: MouseEvent): void => {
      if (!dragged && Math.abs(e.clientX - startX) >= thresholdPx) {
        dragged = true
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUpLight)
        e.preventDefault()
        this._startMoveSlotDrag(dayId, slotIndex, e)
      }
    }

    const onUpLight = (): void => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUpLight)
    }

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUpLight)
  }

  /** Avoid toggling the slot menu from the click that follows a completed move drag. */
  private _swallowFollowingClickForSlotMenu(): void {
    const swallow = (ev: Event): void => {
      ev.stopImmediatePropagation()
      document.removeEventListener('click', swallow, true)
    }
    document.addEventListener('click', swallow, true)
    setTimeout(() => document.removeEventListener('click', swallow, true), 0)
  }

  /** Drag whole slot (move without stretching) when grabbing its middle after threshold exceeded */
  private _startMoveSlotDrag(dayId: string, slotIndex: number, $event: MouseEvent): void {
    $event.preventDefault()
    $event.stopPropagation()
    if (this.isDayTimingsSaving(dayId)) return
    const ctx = this._getDaySlotContext(dayId, slotIndex)
    if (!ctx) return
    const { slots, slot } = ctx
    const { start, end } = this.slotTime(slot)
    const startHours = this._parseTime(start)
    const endHours = this._parseTime(end)
    const duration = endHours - startHours
    const prevSlot = slots[slotIndex - 1]
    const nextSlot = slots[slotIndex + 1]
    const prevEnd = prevSlot ? this._parseTime(this.slotTime(prevSlot).end) : 0
    const nextStart = nextSlot ? this._parseTime(this.slotTime(nextSlot).start) : 24

    this._beginDragSession(dayId, slotIndex, 'move')

    const startMouseHoursSmooth = this._clientXToHoursSmooth($event.clientX)
    const startMouseHours = startMouseHoursSmooth === null ? startHours : startMouseHoursSmooth
    const offset = startHours - startMouseHours

    const clampStart = (candidate: number): number => {
      const min = prevEnd
      const max = nextStart - duration
      return Math.max(min, Math.min(candidate, max))
    }

    const applyMove = (clientX: number): void => {
      const raw = this._clientXToHoursSmooth(clientX)
      if (raw == null) return
      const candidate = raw + offset
      const newStart = clampStart(candidate)
      this.previewResize.set({ dayId, slotIndex, edge: 'move', hours: newStart })
    }
    this._attachPointerDrag($event, applyMove, (clientX) => {
      const raw = this._clientXToHoursSmooth(clientX)
      if (raw == null) return
      const candidate = raw + offset
      const newStart = this._snapTo15Min(clampStart(candidate))
      const newEnd = newStart + duration
      const movedSlot: EventSlot = {
        ...slot,
        from: this._clockFromSnappedDecimalHours(newStart),
        to: this._toSlotEndClockString(newEnd),
        label: slot.label ?? ''
      }
      const newSlots = [...slots]
      newSlots[slotIndex] = movedSlot
      this.slotsChange.emit({ dayId, slots: this._mergeAdjacentSlotsAfterEdit(newSlots, slotIndex) })
      this._swallowFollowingClickForSlotMenu()
    })
  }

  onCellHover(dayId: string, hour: number, over: boolean): void {
    this.hoveredCell.set(over ? { dayId, hour } : null)
  }

  onAddSlotClick(dayId: string, hour: number): void {
    if (this.isDayTimingsSaving(dayId)) return
    const startTime = this.timeLabel(hour)
    this.addSlot.emit({ dayId, startTime })
  }

  isSlotMenuOpen(dayId: string, slotIndex: number): boolean {
    return this.slotMenuOpenKey() === `${dayId}-${slotIndex}`
  }

  onSlotMenuOpened(dayId: string, slotIndex: number): void {
    this.slotMenuOpenKey.set(`${dayId}-${slotIndex}`)
  }

  onSlotMenuClosed(dayId: string, slotIndex: number): void {
    const key = `${dayId}-${slotIndex}`
    if (this.slotMenuOpenKey() === key) this.slotMenuOpenKey.set(null)
  }

  onSlotMenuOption(dayId: string, slotIndex: number, option: DropdownOption): void {
    if (this.isDayTimingsSaving(dayId)) return
    const key = option.key
    if (key === 'edit') this.slotEditOpen.emit({ dayId, slotIndex })
    else if (key === 'remove') {
      this._prepareForSlotRemoval()
      this.slotRemove.emit({ dayId, slotIndex })
    }
  }

  public isHovered(dayId: string, hour: number): boolean {
    return this.hoveredCell() !== null && this.hoveredCell().dayId === dayId && this.hoveredCell().hour === hour
  }

  /** Check if a slot covers this hour (for overlap / empty cell)
   * @param dayId - The ID of the day.
   * @param slotIndex - The index of the slot.
   * @returns `true` if the slot covers the hour, `false` otherwise.
   */
  public isSlotPreviewActive(dayId: string, slotIndex: number): boolean {
    if (this.resizing() && this.resizing()?.dayId === dayId && this.resizing()?.slotIndex === slotIndex) return true
    return !!(this.previewResize() && this.previewResize()?.dayId === dayId && this.previewResize()?.slotIndex === slotIndex)
  }

  slotCoversHour(slots: EventSlot[], hour: number): boolean {
    const hourStart = hour
    const hourEnd = hour + 1
    return (slots ?? []).some((s) => {
      const { start, end } = this.slotTime(s)
      const sStart = this._parseTime(start)
      const sEnd = this._parseTime(end)
      return sStart < hourEnd && sEnd > hourStart
    })
  }

  /**
   * @description Initialize the component.
   */
  private _initialize(): void {
    this._computeSlotEdgeConnections()

    effect(() => {
      const r = this.resizing()
      const host = this.el?.nativeElement as HTMLElement
      if (host) {
        if (r) host.classList.add('tw-cursor-ew-resize')
        else host.classList.remove('tw-cursor-ew-resize')
      }
    })

    effect(() => {
      const noTransition = this.disableSlotLayoutTransition()
      const host = this.el?.nativeElement as HTMLElement
      if (host) {
        if (noTransition) host.classList.add('scheduler-slot-layout-no-transition')
        else host.classList.remove('scheduler-slot-layout-no-transition')
      }
    })

    effect(() => {
      const days = this.days() ?? []
      const nextKeys = new Set<string>()
      const nextOrder = new Map<string, string[]>()
      for (const d of days) {
        const order: string[] = []
        for (const s of d.slots ?? []) {
          const k = this._slotKey(d.id, s)
          nextKeys.add(k)
          order.push(k)
        }
        nextOrder.set(d.id, order)
      }

      if (!this._seenSlotKeysOnce) {
        this._seenSlotKeysOnce = true
        this._prevSlotKeys = nextKeys
        this._prevDaySlotOrder = nextOrder
        return
      }

      const added: string[] = []
      nextKeys.forEach((k) => {
        if (!this._prevSlotKeys.has(k)) added.push(k)
      })

      const removed: string[] = []
      this._prevSlotKeys.forEach((k) => {
        if (!nextKeys.has(k)) removed.push(k)
      })

      const orderChanges: Array<{ dayId: string; prev: string[]; next: string[] }> = []
      nextOrder.forEach((next, dayId) => {
        const prev = this._prevDaySlotOrder.get(dayId) ?? []
        if (prev.join('||') !== next.join('||')) orderChanges.push({ dayId, prev, next })
      })
      this._prevSlotKeys = nextKeys
      this._prevDaySlotOrder = nextOrder

      if (removed.length) {
        this.disableSlotLayoutTransition.set(true)
        window.setTimeout(() => this.disableSlotLayoutTransition.set(false), 220)
      }

      if (added.length === 0) return

      const current = new Set(this.justAddedSlotKeys())
      for (const k of added) current.add(k)
      this.justAddedSlotKeys.set(current)

      window.setTimeout(() => {
        this.justAddedSlotKeys.update((set) => {
          const next = new Set(set)
          for (const k of added) next.delete(k)
          return next
        })
      }, 160)
    })

    effect(() => {
      const rows = this.dayRows()
      const sel = this.selectedMobileDayId()
      if (rows.length === 0) {
        if (sel !== null) this.selectedMobileDayId.set(null)
        return
      }
      const valid = rows.some((r) => r.id === sel)
      if (sel == null || !valid) {
        this.selectedMobileDayId.set(rows[0].id)
      }
    })
  }

  /** Compute slot edge connections */
  private _computeSlotEdgeConnections(): void {
    effect(() => {
      const rows = this.dayRows()
      const map = new Map<string, { prev: boolean; next: boolean }>()
      for (let i = 0; i < rows.length; i++) {
        const day = rows[i]
        const prev = i > 0 ? rows[i - 1] : null
        const next = i < rows.length - 1 ? rows[i + 1] : null
        const canConnectPrev = this._dayHasEndOfDaySlot(prev)
        const canConnectNext = this._dayHasStartOfDaySlot(next)
        for (const slot of day.slots ?? []) {
          const { start, end } = this.slotTime(slot)
          const key = this._slotKey(day.id, slot)
          const edge = {
            prev: start === '00:00' && canConnectPrev,
            next: end === '23:59' && canConnectNext
          }
          if (edge.prev || edge.next) map.set(key, edge)
        }
      }
      this._slotEdgeConnectByKey.set(map)
    })
  }
}
